import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

interface Props {
  hasError?: boolean;
  value: string;
  onChange: (value: string) => void;
}

const DateField = ({ hasError, onChange, value }: Props) => {
  const [isDayFocused, setIsDayFocused] = useState(false);
  const [isMonthFocused, setIsMonthFocused] = useState(false);
  const [isYearFocused, setIsYearFocused] = useState(false);
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const focusGivenInput = useCallback((ref: HTMLInputElement | null) => {
    ref?.focus();
  }, []);

  const dayValue = useMemo(() => {
    if (!!value) {
      const splitted = value.split("-");
      if (!!splitted[2]) return splitted[2];
      return "";
    }
    return "";
  }, [value]);

  const monthValue = useMemo(() => {
    if (!!value) {
      const splitted = value.split("-");
      if (!!splitted[1]) return splitted[1];
      return "";
    }
    return "";
  }, [value]);

  const yearValue = useMemo(() => {
    if (!!value) {
      const splitted = value.split("-");
      if (!!splitted[0]) return splitted[0];
      return "";
    }
    return "";
  }, [value]);

  const shouldShowInputs = useMemo(() => {
    return (
      isDayFocused ||
      isMonthFocused ||
      isYearFocused ||
      !!dayValue ||
      !!monthValue ||
      !!yearValue
    );
  }, [
    isDayFocused,
    isMonthFocused,
    isYearFocused,
    dayValue,
    monthValue,
    yearValue,
  ]);

  const dayValueChange = useCallback(
    (value: string) => {
      const parsedInt = parseInt(value, 10);
      if (isNaN(parsedInt) && !!value) return;
      if (value.length <= 2) onChange(`${yearValue}-${monthValue}-${value}`);
    },
    [monthValue, yearValue, onChange]
  );

  useEffect(() => {
    if (dayValue.length === 2) focusGivenInput(monthRef.current);
  }, [dayValue, focusGivenInput]);

  const onDayBlur = useCallback(() => {
    const parsedDay = parseInt(dayValue, 10);
    if (!isNaN(parsedDay)) {
      if (parsedDay < 1) onChange(`${yearValue}-${monthValue}-01`);
      else if (parsedDay < 10)
        onChange(`${yearValue}-${monthValue}-0${parsedDay}`);
      else if (parsedDay >= 32) onChange(`${yearValue}-${monthValue}-31`);
    }
    setIsDayFocused(false);
  }, [monthValue, yearValue, dayValue, onChange]);

  const monthValueChange = useCallback(
    (value: string) => {
      const parsedInt = parseInt(value, 10);
      if (isNaN(parsedInt) && !!value) return;
      if (value.length <= 2) onChange(`${yearValue}-${value}-${dayValue}`);
    },
    [onChange, yearValue, dayValue]
  );

  const onMonthBlur = useCallback(() => {
    const parsedMonth = parseInt(monthValue, 10);
    if (!isNaN(parsedMonth)) {
      if (parsedMonth < 1) onChange(`${yearValue}-01-${dayValue}`);
      else if (parsedMonth < 10)
        onChange(`${yearValue}-0${parsedMonth}-${dayValue}`);
      else if (parsedMonth >= 13) onChange(`${yearValue}-12-${dayValue}`);
    }
    setIsMonthFocused(false);
  }, [monthValue, yearValue, dayValue, onChange]);

  useEffect(() => {
    if (monthValue.length === 2) focusGivenInput(yearRef.current);
  }, [monthValue, focusGivenInput]);

  const yearValueChange = useCallback(
    (value: string) => {
      const parsedInt = parseInt(value, 10);
      if (isNaN(parsedInt) && !!value) return;
      if (value.length <= 4) onChange(`${value}-${monthValue}-${dayValue}`);
    },
    [dayValue, monthValue, onChange]
  );

  const monthSwitchInputsWithRemoval = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Backspace" || !!monthValue || !dayValue) return;
      focusGivenInput(dayRef.current);
    },
    [focusGivenInput, dayValue, monthValue]
  );

  const yearSwitchInputsWithRemoval = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Backspace" || !!yearValue || !monthValue) return;
      focusGivenInput(monthRef.current);
    },
    [focusGivenInput, yearValue, monthValue]
  );

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation();
    },
    []
  );

  return (
    <MainContainer onClick={() => focusGivenInput(dayRef.current)}>
      {!shouldShowInputs && (
        <Placeholder hasError={hasError}>DD/MM/YYYY</Placeholder>
      )}
      <InputContainer>
        <NarrowInput
          hasError={hasError}
          type="text"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            dayValueChange(e.target.value)
          }
          autoCorrect="off"
          autoComplete="off"
          value={dayValue}
          ref={dayRef}
          onBlur={onDayBlur}
          onFocus={() => setIsDayFocused(true)}
        />
        {shouldShowInputs && <Divider>/</Divider>}
        <NarrowInput
          hasError={hasError}
          type="text"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            monthValueChange(e.target.value)
          }
          autoCorrect="off"
          autoComplete="off"
          ref={monthRef}
          value={monthValue}
          onBlur={onMonthBlur}
          onKeyDown={monthSwitchInputsWithRemoval}
          onClick={onClickHandler}
          onFocus={() => setIsMonthFocused(true)}
        />
        {shouldShowInputs && <Divider>/</Divider>}
        <WideInput
          hasError={hasError}
          type="text"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            yearValueChange(e.target.value)
          }
          autoCorrect="off"
          autoComplete="off"
          ref={yearRef}
          value={yearValue}
          onKeyDown={yearSwitchInputsWithRemoval}
          onClick={onClickHandler}
          onFocus={() => setIsYearFocused(true)}
          onBlur={() => setIsYearFocused(false)}
        />
      </InputContainer>
    </MainContainer>
  );
};

const InputContainer = styled.div`
  display: flex;
`;

const Divider = styled.div``;

const StyledInput = styled.input<{ hasError?: boolean }>`
  border: 0px;
  padding: 0px;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  text-align: center;
  outline: 0;
  color: ${({ hasError, theme }) =>
    hasError ? theme.redesign.errorColor : theme.redesign.defaultColor};
  font-size: 14px;
`;

const WideInput = styled(StyledInput)`
  width: 40px;
`;

const NarrowInput = styled(StyledInput)`
  width: 20px;
`;

const MainContainer = styled.div`
  width: calc(100% - 40px);
  height: 48px;
  padding: 0 20px;
  border-radius: 48px;
  background-color: ${({ theme }) => theme.redesign.defaultBackground};
  font-family: ${({ theme }) => theme.redesign.regularFont};
  display: flex;
  align-items: center;
  font-size: 14px;
  cursor: text;
`;

const Placeholder = styled.div<{ hasError?: boolean }>`
  color: ${({ theme, hasError }) =>
    hasError
      ? theme.redesign.errorColor
      : theme.redesign.form.textField.placeholderColor};
`;
export default DateField;
